import axios from 'axios'
import { message } from 'antd'
import { API_URL } from '../app/constants/APIKEYS'
import { optimizeFormDataVideos } from '../app/utils/videoOptimizer'
import { ACCESS_TOKEN, TV_CREDENTIALS_KEY, TV_MODE_KEY, TOKEN_REFRESH_EVENT } from '../app/constants/LOCALKEYS'

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024
const IMAGE_SIZE_ERROR = '이미지는 최대 5MB까지 업로드할 수 있습니다.'

type FormdataRequestOptions = {
	skipVideoOptimization?: boolean
}

const isImageFile = (file: File) => {
  if (!file) return false
  if (file.type && file.type.startsWith('image/')) return true
  const ext = file.name?.split('.').pop()?.toLowerCase()
  return !!ext && ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif', 'avif'].includes(ext)
}

const hasOversizedImage = (data: any): { exceeded: boolean; names: string[] } => {
  if (typeof FormData === 'undefined' || !(data instanceof FormData)) {
    return { exceeded: false, names: [] }
  }

  const oversizedNames: string[] = []
  data.forEach(value => {
    if (value instanceof File && isImageFile(value) && value.size > MAX_IMAGE_SIZE_BYTES) {
      oversizedNames.push(value.name || '이미지')
    }
  })

  return { exceeded: oversizedNames.length > 0, names: oversizedNames }
}
export default class API {
	/**
	 * API발송 메소드, /v1 + url
	 */
	static async send(
		method: string,
		url: string,
		token: string | null,
		data: any,
		logout: boolean = true,
		apiurl?: string,
		retryCount = 0
	) {
		try {
			let headers = this.getHeaders(token)
			let options = this.getOptions(method, headers, data)
			let res = await fetch((apiurl ? apiurl : API_URL) + url, options)
				.then(async res => {
					if (res.status === 401) {
						return this.handleUnauthorized(method, url, token, data, logout, apiurl, retryCount);
					}

					if (res.status === 403) {
						throw new Error('접근권한이 없습니다')
					}

					return res.json()
				})
				.then(res => {
					if (!res.code) {
					}

					if (res.code >= 500) {
						throw new Error(res.result.error || '')
					}

					return res
				})

			return res
		} catch (e: any) {
			// console.log(e)
			// if (logout) this.forceLogout()
			console.log(e)
			throw new Error(e?.message)
		}
	}

	static async postFile(
		url: string,
		token: string | null,
		data: any,
		logout: boolean = true,
		apiurl?: string
	) {
		try {
			let headers = this.getHeaderToken(token)
			let options = this.getOptions('post', headers, data)

			let res = await fetch((apiurl ? apiurl : API_URL) + url, options)
				.then(res => {
					return res.json()
				})
				.then(res => {
					if (res.code === 503) {
						if (logout) this.forceLogout()
					}
					return res
				})
			return res
		} catch (e) {
			console.log(e)
			this.forceLogout()
		}
	}

	static async postFormdata(
		method: string,
		url: string,
		token: string | null,
		data: any,
		logout: boolean = true,
		apiurl?: string,
		options?: FormdataRequestOptions
	) {
		let closeEncodingMessage: (() => void) | undefined
		try {
			let payload = data

			let skipServerOptimize = options?.skipVideoOptimization === true

			if (!skipServerOptimize && typeof FormData !== 'undefined' && data instanceof FormData) {
				const { formData, converted, hasVideo } = await optimizeFormDataVideos(data, {
					onStart: () => {
						if (!closeEncodingMessage) {
							closeEncodingMessage = message.loading('영상 파일을 최적화하는 중입니다...', 0)
						}
					},
					onEnd: () => {
						if (closeEncodingMessage) {
							closeEncodingMessage()
							closeEncodingMessage = undefined
						}
					},
				})
				payload = formData
				if (!converted && closeEncodingMessage) {
					closeEncodingMessage()
					closeEncodingMessage = undefined
				}
				skipServerOptimize = hasVideo && !converted
			}

			const targetUrl =
				skipServerOptimize
					? `${url}${url.includes('?') ? '&' : '?'}optimize=false`
					: url

			const fullUrl = (apiurl ? apiurl : API_URL) + targetUrl

			let res = await fetch(fullUrl, {
				method: method,
				headers: { Authorization: 'Bearer ' + token },
				body: payload,
			})
				.then(async res => {
					if (res.status === 401) {
						return this.handleUnauthorized(method, targetUrl, token, payload, logout, apiurl, 0)
					}

					if (res.status === 403) {
						throw new Error('접근권한이 없습니다')
					}

					return res.json()
				})
				.then(res => {
					if (!res.code) {
					}

					if (res.code >= 500) {
						throw new Error(res.result.error || '')
					}

					return res
				})

			if (closeEncodingMessage) {
				closeEncodingMessage()
			}

			return res
		} catch (e: any) {
			// console.log(e)
			// if (logout) this.forceLogout()
			console.log(e)
			throw new Error(e?.message)
		}
	}

	static async postImage(
		method: 'POST' | 'PUT',
		url: string,
		token: string | null,
		data: any,
		apiurl?: string,
		options?: FormdataRequestOptions
	) {
		const { exceeded, names } = hasOversizedImage(data)
		if (exceeded) {
			const suffix = names.length ? ` (${names.join(', ')})` : ''
			message.error(`${IMAGE_SIZE_ERROR}${suffix}`)
			return Promise.reject(new Error('IMAGE_SIZE_EXCEEDED'))
		}

		return this.postFormdata(method, url, token, data, true, apiurl, options)
	}

	static async get(
		url: string,
		token: string | null,
		data: any,
		logout: boolean = true,
		apiurl?: string
	) {
		return this.send('GET', url, token, data, logout, apiurl)
	}
	static async post(
		url: string,
		token: string | null,
		data: any,
		apiurl?: string
	) {
		return this.send('POST', url, token, data, true, apiurl)
	}
	static async patch(
		url: string,
		token: string | null,
		data: any,
		apiurl?: string
	) {
		return this.send('PATCH', url, token, data, true, apiurl)
	}


	static async postForm(
		url: string,
		token: string | null,
		data: any,
		apiurl?: string
	) {
		return this.send('POST', url, token, data, true, apiurl)
	}

	static async delete(
		url: string,
		token: string | null,
		data: any,
		apiurl?: string
	) {
		return this.send('DELETE', url, token, data, true, apiurl)
	}
	static async put(
		url: string,
		token: string | null,
		data: any,
		apiurl?: string
	) {
		return this.send('PUT', url, token, data, true, apiurl)
	}

	static async getPublic(url: string) {
		return this.send('GET', url, null, {})
	}
	static async postPublic(url: string, data: any) {
		return this.send('POST', url, null, data)
	}
	static async deletePublic(url: string, data: any) {
		return this.send('DELETE', url, null, data)
	}
	static async putPublic(url: string, data: any) {
		return this.send('PUT', url, null, data)
	}

	static async refreshToken(token: string) {
		try {
			let options = this.getOptions('POST', this.getHeaders(token), {
				token,
			})

			let res = await fetch(API_URL + '/auth/refresh', options)
				.then(res => {
					return res.json()
				})
				.then(res => {
					if (res.code !== 0) {
						return false
					}
					return res
				})
			return res
		} catch (e) {
			console.log(e)
			this.forceLogout()
		}
	}

	static getHeaders(token: string | null) {
		const headers: any = token
			? {
				Authorization: 'Bearer ' + token,
				'Content-Type': 'application/json',

			}
			: {
				'Content-Type': 'application/json',
			}
		return headers
	}


	static getHeaderToken(token: string | null) {
		const headers: any = token
			? {
				Authorization: 'Bearer ' + token

			}
			: {
				Authorization: 'Bearer ',
			}
		return headers
	}

	static getOptions(method: string, headers: any, data?: any) {
		const options =
			method.toLocaleUpperCase() === 'GET'
				? {
					method: method,
					headers: headers,
				}
				: {
					method: method,
					headers: headers,
					body: JSON.stringify(data),
				}
		return options
	}

	static forceLogout() {
		window.location.href = '/admin/logout'
	}

	private static async handleUnauthorized(method: string, url: string, token: string | null, data: any, logout: boolean, apiurl?: string, retryCount = 0) {
		const relogged = await this.tryTvAutoLogin();
		if (relogged && retryCount === 0) {
			const nextToken = this.getStoredToken();
			return await this.send(method, url, nextToken, data, logout, apiurl, retryCount + 1);
		}

		if (logout) {
			alert('로그인이 만료되었습니다');
			this.forceLogout();
		}
		throw new Error('로그인을 확인해 주세요')
	}

	private static getStoredToken(): string | null {
		if (typeof window === 'undefined') return null;
		return window.localStorage.getItem(ACCESS_TOKEN);
	}

	private static async tryTvAutoLogin(): Promise<boolean> {
		if (typeof window === 'undefined') return false;
		try {
			const tvMode = window.localStorage.getItem(TV_MODE_KEY);
			if (tvMode !== '1') return false;

			const saved = window.localStorage.getItem(TV_CREDENTIALS_KEY);
			if (!saved) return false;

			const credentials = JSON.parse(saved);
			if (!credentials?.email || !credentials?.password) return false;

			const response = await fetch(API_URL + '/auth/signin', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email: credentials.email, password: credentials.password }),
			});

			if (!response.ok) return false;

			const payload = await response.json();
			const token = payload?.result?.result?.token;

			if (payload?.result?.success && token) {
				window.localStorage.setItem(ACCESS_TOKEN, token);
				window.dispatchEvent(new CustomEvent(TOKEN_REFRESH_EVENT, { detail: token }));
				return true;
			}
			return false;
		} catch (error) {
			console.error('TV auto login failed', error);
			return false;
		}
	}
}
